/**
 * Polling-state presentation for wizard steps that await a dispatched
 * backend job inside `onBeforeNext`/`onConfirm`.
 *
 * While such a poll runs, the awaited promise holds the wizard's
 * `isSubmitting` flag — which disables every footer CTA and the header close
 * button. `WizardPollingBody` swaps the step's idle body for a centered
 * status view (design-system `EmptyState` + `LoadingIndicator`) for the
 * duration, with an optional escape-hatch action — without it, a long job
 * would trap the user in the wizard until they unmount it.
 *
 * The escape hatch is intentionally labelled "Stop waiting", not "Cancel":
 * these dispatched jobs have no server-side cancellation endpoint, so the
 * action only stops observing the poll — the backend job keeps running and
 * its result still lands. Keep any relabel equally honest.
 *
 * The idle body unmounts while the status view shows; that loses nothing as
 * long as the step's state (form values, selections) lives in the parent
 * wizard state rather than in the step components — which the wizard
 * architecture already requires.
 */

import type { ReactNode } from 'react';

import { EmptyState } from '@/lib/base/empty-state';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';

import { useWizardContext } from './wizard-context';

export interface WizardPollingStateProps {
  /** Heading, e.g. "Analyzing your data…". */
  title: string;
  /** Supporting copy — say what is happening and set the time expectation. */
  description: string;
  /**
   * Escape-hatch action (e.g. abort the poll and close the wizard). Omit only
   * when the wizard remains exitable during the poll by other means.
   */
  onCancel?: () => void;
  /** Escape-hatch label. Default: "Stop waiting" (see module docstring). */
  cancelLabel?: string;
}

/**
 * The centered polling status view itself, context-free — render it directly
 * when you own the polling state, or via `WizardPollingBody` to key off the
 * wizard's `isSubmitting`.
 */
export function WizardPollingState({
  title,
  description,
  onCancel,
  cancelLabel = 'Stop waiting',
}: WizardPollingStateProps) {
  return (
    <EmptyState
      icon={<LoadingIndicator size='lg' type='line-simple' />}
      title={title}
      description={description}
      action={onCancel ? { label: cancelLabel, onClick: onCancel } : undefined}
    />
  );
}

export interface WizardPollingBodyProps extends WizardPollingStateProps {
  /** The step's idle body, rendered whenever no poll is in flight. */
  children: ReactNode;
}

/**
 * Renders the step's idle body normally, and `WizardPollingState` instead
 * while the wizard's `isSubmitting` flag is held (i.e. a dispatched job's
 * poll is being awaited in `onBeforeNext`/`onConfirm`).
 */
export function WizardPollingBody({
  children,
  ...stateProps
}: WizardPollingBodyProps) {
  const { isSubmitting } = useWizardContext();
  if (!isSubmitting) {
    return children;
  }
  return <WizardPollingState {...stateProps} />;
}
